import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { DnaOligo } from 'src/models';
import {
  PLATE_POOLING,
  PLATE_POOLING_OPTIONS,
  REMAINDER_CHECKED,
  PLATE_POOLING_NAMESPACE,
  POOLED_PLATES,
  STEPS,
  PLATES_FOR_POOLING,
  PLATE_POOLING_NAMESPACE_ACTION,
  NORMALIZATION_ALIQUOTS_NAMESPACE,
  NORMALIZATION_OPTIONS,
  NORMALIZATION,
  PLATE_UPLOADED_NAMESPACE,
  PLATE_SIZE_DETAILS_NAMESPACE
} from 'src/constants/forms';
import { selectStateData } from 'src/core/redux/selectors';
import { addDetails as addDetailsAction } from 'src/actions';
import { DoubleSideLayout } from 'src/components';
import { SummaryCard } from 'src/components/OligoDNA/components';

import messages from './messages';
import PlatePoolingComponent from './PlatePoolingComponent';

const getPlatePoolingConfig = (platesForPooling, volumeConcetrationSelected) => {
  const platesPoolingCount = platesForPooling?.length || -1;
  const disabled = platesPoolingCount % 2 !== 0 || !volumeConcetrationSelected;
  const tooltipMessage = disabled ? messages.pooledPlatesOddInfo : {};

  if (platesPoolingCount === -1) {
    return [];
  }

  return [{
    label: messages.none,
    value: PLATE_POOLING_OPTIONS.NONE,
  }, {
    label: messages.halves,
    value: PLATE_POOLING_OPTIONS.HALVES,
    disabled,
    tooltipMessage,
  }, {
    label: messages.oddEven,
    value: PLATE_POOLING_OPTIONS.ODD_EVEN,
    disabled,
    tooltipMessage,
  }, {
    label: messages.custom,
    value: PLATE_POOLING_OPTIONS.CUSTOM,
    disabled,
    tooltipMessage,
  }];
};

const PlatePoolingContainer = ({
  formData,
  addFormData,
  volumeConcetrationSelected,
  platesForPooling = {},
  plateSizeDetails = {}
}) => {
  const platePooling = formData?.[PLATE_POOLING]?.value;
  const onChangeData = (value, fieldName, isValid) => addFormData({
    ...formData,
    [fieldName]: { value, isValid },
  });

  React.useEffect(() => {
    if (!platePooling) {
      addFormData(DnaOligo.getDefaultFormState(STEPS.PLATE_POOLING));
    } else if (platePooling === PLATE_POOLING_OPTIONS.NONE) {
      addFormData({
        [REMAINDER_CHECKED]: { value: false, isValid: true },
      });
    } else if (platePooling === PLATE_POOLING_OPTIONS.CUSTOM) {
      addFormData({
        [POOLED_PLATES]: { value: [], isValid: true },
        [PLATES_FOR_POOLING]: { value: platesForPooling, isValid: true }
      });
    } else if (platePooling === PLATE_POOLING_OPTIONS.HALVES ||
      platePooling === PLATE_POOLING_OPTIONS.ODD_EVEN) {
      addFormData({
        [PLATES_FOR_POOLING]: { value: platesForPooling, isValid: true }
      });
    }
  }, [platePooling, addFormData, platesForPooling]);

  React.useEffect(() => {
    if (volumeConcetrationSelected === false) {
      addFormData(DnaOligo.getDefaultFormState(STEPS.PLATE_POOLING));
    }
  }, [volumeConcetrationSelected, addFormData]);

  React.useEffect(() => {
    if (!formData) {
      addFormData(DnaOligo.getDefaultFormState(STEPS.PLATE_POOLING));
    }
  }, [addFormData, formData]);

  return (
    <DoubleSideLayout
      leftContent={(
        <PlatePoolingComponent
          formData={formData}
          onChangeData={onChangeData}
          platePoolingConfig={getPlatePoolingConfig(platesForPooling, volumeConcetrationSelected)}
          platesForPooling={platesForPooling}
          plateSizeDetails={plateSizeDetails}
        />
      )}
      rightContent={<SummaryCard />}
    />
  );
};

PlatePoolingContainer.propTypes = {
  formData: PropTypes.object,
  addFormData: PropTypes.func.isRequired,
  volumeConcetrationSelected: PropTypes.bool,
  platesForPooling: PropTypes.array,
  plateSizeDetails: PropTypes.object,
};

const mapStateToProps = (state) => {
  const uploadedPlates = selectStateData(PLATE_UPLOADED_NAMESPACE)(state);

  return ({
    formData: selectStateData(PLATE_POOLING_NAMESPACE)(state),
    volumeConcetrationSelected: (
      selectStateData(NORMALIZATION_ALIQUOTS_NAMESPACE)(state) || {}
    )[NORMALIZATION]?.value === NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME,
    platesForPooling: uploadedPlates?.item?.data?.plateStatuses,
    plateSizeDetails: selectStateData(PLATE_SIZE_DETAILS_NAMESPACE)(state),
  });
};

const mapDispatchToProps = {
  addFormData: addDetailsAction(PLATE_POOLING_NAMESPACE_ACTION),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PlatePoolingContainer);
